// Mapa_Veiculos.jsx
// Mapa em tempo real da frota (ObterPosicaoVeiculo)
//
// Requisitos:
//   - @mui/material
//   - react-leaflet
//   - leaflet
//   - (opcional) @mui/icons-material para ícones de busca/refresh

import * as React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Stack,
  Tooltip,
  Button,
  ButtonGroup,
  Popover,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import RoomIcon from '@mui/icons-material/Room';
import { useTheme } from '@mui/material/styles';

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Tooltip as LeafletTooltip,
  useMap,
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { LOCALIZACOES_FIXAS } from '../components/Localizacoes';

// ===== Config de ambiente (igual Setor3Page) =====
const API_BASE =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  '';

const API_KEY =
  (typeof import.meta !== 'undefined' &&
    import.meta.env &&
    import.meta.env.VITE_API_KEY) ||
  process.env.REACT_APP_API_KEY ||
  '';

// ===== Constantes =====
const REFRESH_SECONDS = 30;

// Coordenadas aproximadas de Manaus para centro inicial
const MANAUS_CENTER = {
  lat: -3.019545,
  lng: -60.026674,
};

// Ícone de ônibus usando HTML: azul (em movimento) e vermelho (parado)
// Ícone de ônibus usando HTML: azul (em movimento) e vermelho (parado)
function createBusIcon(isMoving, isQuebrado = false, isOciosoFechado = false) {
  let bgColor = isMoving ? '#1976d2' : '#d32f2f'; // azul / vermelho (parado)
  let borderColor = 'white';

  // Se o veículo estiver em ocioso fechado, pintamos em #B4FF07
  if (isOciosoFechado) {
    bgColor = '#B4FF07';
    borderColor = 'white';
  }

  // Se o veículo estiver em quebra, destacamos com amarelo (prioridade maior)
  if (isQuebrado) {
    bgColor = '#f9a825'; // amarelo
    borderColor = '#ffeb3b';
  }

  return L.divIcon({
    className: 'custom-bus-icon',
    html: `
      <div style="
        background:${bgColor};
        color:white;
        width:28px;
        height:28px;
        border-radius:50%;
        display:flex;
        align-items:center;
        justify-content:center;
        border:2px solid ${borderColor};
        box-shadow:0 0 4px rgba(0,0,0,0.5);
        font-size:16px;
      ">
        🚍
      </div>
    `,
    iconSize: [28, 28],
    iconAnchor: [14, 28],
    popupAnchor: [0, -28],
  });
}


// Ícone para pontos fixos (garagens, terminais, finais de linha etc.)
function createFixedPointIcon(ponto) {
  const tipoRaw = ponto?.tipo || 'TERMINAL';
  const t = tipoRaw.toString().toUpperCase();

  // cor padrão por tipo
  let bgColor = '#4D5D53'; // padrão: cinza
  let emoji = '🏢';
  let label = '';

  if (t === 'GARAGEM') {
    // Garagem = empresa de ônibus
    bgColor = '#05FFA1'; // default se não tiver cor no dicionário
    emoji = '🏤';
    label = 'G';
  } else if (t === 'TERMINAL') {
    // Terminal = local de início/fim das viagens
    bgColor = '#6FCB9F';
    emoji = '🚏';
    label = '';
  } else if (t === 'FINAL DE LINHA') {
    bgColor = '#EAC086';
    emoji = '🏁';
    label = '';
  }

  // Se no dicionário tiver uma cor específica, ela prevalece
  if (ponto && ponto.cor) {
    bgColor = ponto.cor;
  }

  return L.divIcon({
    className: 'fixed-point-icon',
    html: `
      <div style="
        background:${bgColor};
        color:white;
        min-width:30px;
        height:30px;
        padding:0 4px;
        border-radius:16px;
        display:flex;
        align-items:center;
        justify-content:center;
        gap:2px;
        border:2px solid white;
        box-shadow:0 0 4px rgba(0,0,0,0.5);
        font-size:16px;
        font-weight:600;
      ">
        <span>${emoji}</span>
        ${label ? `<span style="font-size:12px;">${label}</span>` : ''}
      </div>
    `,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
    popupAnchor: [0, -30],
  });
}

// ===== Helpers =====
const norm = (v) => (v ?? '').toString().trim();
const normCode = (v) => (v ?? '').toString().trim().toUpperCase();


function formatDateTimeBR(ms) {
  if (!ms) return '';
  try {
    const d = new Date(Number(ms));
    if (Number.isNaN(d.getTime())) return '';
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'short',
      timeStyle: 'short',
    }).format(d);
  } catch {
    return '';
  }
}

// Normaliza o objeto vindo do ObterPosicaoVeiculo
function normalizeVeiculo(raw) {
  const lat = Number(raw.latitude);
  const lng = Number(raw.longitude);

  return {
    raw,
    codigo: norm(raw.codigo),
    placa: norm(raw.placa),
    linha: norm(raw.linha),
    trajeto: norm(raw.trajeto),
    sentido: norm(raw.sentido),
    velocidade: Number(raw.velocidade) || 0,
    ignicao: Number(raw.ignicao) || 0,
    hodometro: Number(raw.hodometro) || 0,
    direcao: Number(raw.direcao) || 0,
    dataHora: raw.dataHora,
    latitude: Number.isFinite(lat) ? lat : null,
    longitude: Number.isFinite(lng) ? lng : null,
  };
}

// Distância aproximada em metros entre duas coordenadas (Haversine)
function distanceMeters(lat1, lng1, lat2, lng2) {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371000; // raio da Terra em metros
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) *
      Math.cos(toRad(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

// Encontra o "centro" da garagem como o ponto com mais vizinhos parados em até 250m
function findGarageCenter(veiculos) {
  const parados = veiculos.filter(
    (v) =>
      v.latitude != null &&
      v.longitude != null &&
      v.velocidade === 0
  );

  if (parados.length === 0) return null;

  let best = null;
  let bestCount = 0;

  parados.forEach((v) => {
    let count = 0;
    parados.forEach((u) => {
      const d = distanceMeters(
        v.latitude,
        v.longitude,
        u.latitude,
        u.longitude
      );
      if (d <= 250) {
        count += 1;
      }
    });

    if (count > bestCount) {
      bestCount = count;
      best = v;
    }
  });

  return best
    ? { lat: best.latitude, lng: best.longitude }
    : null;
}

// Busca o array de veículos da API de tráfego
async function fetchPosicoesVeiculos({ signal } = {}) {
  if (!API_BASE) {
    throw new Error(
      'API_BASE não configurada. Verifique VITE_API_BASE ou REACT_APP_API_BASE.'
    );
  }

  const url = `${API_BASE}/api/trafego/posicao-veiculo`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,
      'x-api-key': API_KEY,
    },
    signal,
  });

  if (!res.ok) {
    const text = await res.text();
    throw new Error(
      `Falha ao carregar posição de veículos (${res.status}) — ${text}`
    );
  }

  const data = await res.json();

  let veiculos = [];
  if (Array.isArray(data)) {
    veiculos = data;
  } else if (data && Array.isArray(data.veiculos)) {
    veiculos = data.veiculos;
  }

  return veiculos
    .map(normalizeVeiculo)
    .filter((v) => v.latitude != null && v.longitude != null);
}

async function fetchQuebrasFormatadas({ signal } = {}) {
  if (!API_BASE) {
    throw new Error(
      'API_BASE não configurada. Verifique VITE_API_BASE ou REACT_APP_API_BASE.'
    );
  }

  const url = `${API_BASE}/api/trafego/quebras/formatadas`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,
      'x-api-key': API_KEY,
    },
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Falha ao buscar quebras (${res.status}) — ${res.statusText || ''} ${text}`
    );
  }

  const data = await res.json();

  // Se a API não devolver array, devolvemos um objeto vazio
  if (!Array.isArray(data)) {
    return {};
  }

  // Mapa por veículo: { [codigoVeiculo]: { veiculo, quebras, totalQuebras } }
  const map = {};

  data.forEach((item) => {
    const veiculo = normCode(item.veiculo); // normaliza código
    if (!veiculo) return;

    const quebras = Array.isArray(item.quebras) ? item.quebras : [];

    if (!map[veiculo]) {
      map[veiculo] = {
        veiculo,
        quebras,
        totalQuebras: quebras.length,
      };
    } else {
      const merged = [...map[veiculo].quebras, ...quebras];
      map[veiculo].quebras = merged;
      map[veiculo].totalQuebras = merged.length;
    }
  });

  return map;
}

async function fetchOciososFechados({ signal } = {}) {
  if (!API_BASE) {
    throw new Error(
      'API_BASE não configurada. Verifique VITE_API_BASE ou REACT_APP_API_BASE.'
    );
  }

  const url = `${API_BASE}/api/trafego/ocioso-fechado`;

  const res = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      Authorization: API_KEY,
      'x-api-key': API_KEY,
    },
    signal,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(
      `Falha ao buscar ociosos fechados (${res.status}) — ${res.statusText || ''} ${text}`
    );
  }

  const data = await res.json();

  let itens = [];
  if (Array.isArray(data)) {
    itens = data;
  } else if (Array.isArray(data.itens)) {
    itens = data.itens;
  } else if (Array.isArray(data.veiculos)) {
    itens = data.veiculos;
  } else if (Array.isArray(data.veiculosRotaZero)) {
    itens = data.veiculosRotaZero;
  }

  const map = {};

  itens.forEach((item) => {
    // prefixo/veiculo/codigo vindo da API de ocioso fechado
    const codigo = normCode(item.prefixo || item.veiculo || item.codigo);
    if (!codigo) return;

    map[codigo] = item;
  });

  return map;
}

// ===== Componente de recentralização do mapa =====
function RecenterMap({ center, zoom }) {
  const map = useMap();
  React.useEffect(() => {
    if (center && center.lat != null && center.lng != null) {
      map.setView(center, zoom ?? map.getZoom(), {
        animate: true,
      });
    }
  }, [center, zoom, map]);
  return null;
}

// ===== Componente principal =====
export default function MapaVeiculos() {
  const theme = useTheme();

  const [veiculos, setVeiculos] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState('');
  const [searchTerm, setSearchTerm] = React.useState('');
  const [lastUpdated, setLastUpdated] = React.useState(() => new Date());
  const [searchPonto, setSearchPonto] = React.useState('');
  const [selectedPonto, setSelectedPonto] = React.useState(null);


  const [anchorGaragemEl, setAnchorGaragemEl] = React.useState(null);

  const [mapStyle, setMapStyle] = React.useState('rua'); // 'rua' ou 'satelite'
  const [quebrasPorVeiculo, setQuebrasPorVeiculo] = React.useState({});
  const [ociososPorVeiculo, setOciososPorVeiculo] = React.useState({});

  const controllerRef = React.useRef(null);
  // Evita disparar múltiplos reloads simultâneos
  const isRefreshingRef = React.useRef(false);

  // Função de carregamento com AbortController para evitar corridas
  const loadVeiculos = async () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
    }
    const controller = new AbortController();
    controllerRef.current = controller;

    try {
      setLoading(true);
      setError('');
      const lista = await fetchPosicoesVeiculos({
        signal: controller.signal,
      });
      setVeiculos(lista);

      try {
        const mapaQuebras = await fetchQuebrasFormatadas({
          signal: controller.signal,
        });
        setQuebrasPorVeiculo(mapaQuebras);
      } catch (errQuebras) {
        console.error('Erro ao buscar quebras:', errQuebras);
        setQuebrasPorVeiculo({});
      }

      try {
        const mapaOciosos = await fetchOciososFechados({
          signal: controller.signal,
        });
        setOciososPorVeiculo(mapaOciosos);
      } catch (errOciosos) {
        console.error('Erro ao buscar ociosos fechados:', errOciosos);
        setOciososPorVeiculo({});
      }

    } catch (err) {
      if (err.name === 'AbortError') return;
      console.error(err);
      setError(
        err.message ||
          'Erro ao carregar posição dos veículos. Tente novamente.'
      );
    } finally {
      // Atualiza sempre o horário da última tentativa de atualização
      setLastUpdated(new Date());
      setLoading(false);
    }
  };

  // 1) Carrega na montagem
  React.useEffect(() => {
    loadVeiculos();
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
    };
  }, []);

  // 2) Auto refresh a cada 30s
  React.useEffect(() => {
    const id = setInterval(() => {
      if (isRefreshingRef.current) return;

      isRefreshingRef.current = true;
      loadVeiculos().finally(() => {
        isRefreshingRef.current = false;
      });
    }, REFRESH_SECONDS * 1000);

    return () => clearInterval(id);
  }, []);

  // Seleção de ponto fixo com base na pesquisa (GARAGEM / TERMINAL / FINAL DE LINHA)
  React.useEffect(() => {
    const term = norm(searchPonto).toLowerCase();
    if (!term) {
      setSelectedPonto(null);
      return;
    }

    const found = LOCALIZACOES_FIXAS.find((p) => {
      const nome = (p.nome || '').toLowerCase();
      const tipo = (p.tipo || '').toLowerCase();
      return (
        nome.includes(term) ||
        tipo.includes(term)
      );
    });

    setSelectedPonto(found || null);
  }, [searchPonto]);

  // Filtro por veículo (código ou placa)
  const filteredVeiculos = React.useMemo(() => {
    const term = norm(searchTerm).toLowerCase();
    if (!term) return veiculos;

    return veiculos.filter((v) => {
      const codigo = v.codigo.toLowerCase();
      const placa = v.placa.toLowerCase();
      return (
        codigo.includes(term) ||
        placa.includes(term)
      );
    });
  }, [veiculos, searchTerm]);

  const veiculosGaragem = React.useMemo(() => {
    if (!veiculos || veiculos.length === 0) return [];

    const centerGarage = findGarageCenter(veiculos);
    if (!centerGarage) return [];

    return veiculos.filter((v) => {
      if (
        v.latitude == null ||
        v.longitude == null ||
        v.velocidade !== 0
      ) {
        return false;
      }
      const dist = distanceMeters(
        centerGarage.lat,
        centerGarage.lng,
        v.latitude,
        v.longitude
      );
      return dist <= 250; // metros ao redor da garagem
    });
  }, [veiculos]);

  // Centro e zoom do mapa:
  // 1) se tiver pesquisa de veículo, foca no primeiro veículo filtrado (zoom mais próximo)
  // 2) senão, se tiver ponto fixo selecionado, centraliza nele (zoom intermediário)
  // 3) senão, usa sempre o centro da cidade (MANAUS_CENTER)
  const { center, zoom } = React.useMemo(() => {
    const termVeiculo = norm(searchTerm).toLowerCase();

    // 1) foco em veículo pesquisado
    if (termVeiculo && filteredVeiculos.length > 0) {
      const v = filteredVeiculos[0];
      if (v.latitude != null && v.longitude != null) {
        return {
          center: {
            lat: v.latitude,
            lng: v.longitude,
          },
          zoom: 16, // zoom mais próximo para ver bem o veículo
        };
      }
    }

    // 2) foco em ponto fixo (garagem / terminal / final de linha)
    if (
      selectedPonto &&
      selectedPonto.latitude != null &&
      selectedPonto.longitude != null
    ) {
      return {
        center: {
          lat: selectedPonto.latitude,
          lng: selectedPonto.longitude,
        },
        zoom: 20, // um pouco mais aberto
      };
    }

    // 3) nada selecionado -> centro fixo de Manaus
    return {
      center: MANAUS_CENTER,
      zoom: 13,
    };
  }, [searchTerm, filteredVeiculos, selectedPonto]);

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleManualRefresh = () => {
    loadVeiculos();
  };

  const openGaragem = Boolean(anchorGaragemEl);
  const handleOpenGaragem = (event) => {
    setAnchorGaragemEl(event.currentTarget);
  };
  const handleCloseGaragem = () => {
    setAnchorGaragemEl(null);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          borderRadius: 3,
          bgcolor: 'background.paper',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        {/* Cabeçalho e controles */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6" fontWeight={600}>
              Mapa de Veículos — Tempo Real
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
            >
              Encontre o veículo em tempo real no mapa.
            </Typography>
          </Box>

          <Stack
            direction="row"
            spacing={2}
            alignItems="center"
          >
            <TextField
              size="small"
              label="Pesquisar veículo"
              placeholder="Ex: 03000"
              value={searchTerm}
              onChange={(e) =>
                setSearchTerm(e.target.value)
              }
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm ? (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={handleClearSearch}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

             <TextField
               size="small"
               label="Pesquisar ponto fixo"
               placeholder="Ex: Garagem, Terminal, Final de Linha"
               value={searchPonto}
               onChange={(e) =>
                 setSearchPonto(e.target.value)
               }
               InputProps={{
                 startAdornment: (
                   <InputAdornment position="start">
                     <SearchIcon fontSize="small" />
                   </InputAdornment>
                 ),
                 endAdornment: searchPonto ? (
                   <InputAdornment position="end">
                     <IconButton
                       size="small"
                       onClick={() => setSearchPonto('')}
                     >
                       <CloseIcon fontSize="small" />
                     </IconButton>
                   </InputAdornment>
                 ) : null,
               }}
             />

            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenGaragem}
              disabled={veiculosGaragem.length === 0}
            >
              Garagem ({veiculosGaragem.length})
            </Button>
            <ButtonGroup
              variant="outlined"
              size="small"
            >
              <Button
                onClick={() => setMapStyle('rua')}
                variant={
                  mapStyle === 'rua'
                    ? 'contained'
                    : 'outlined'
                }
              >
                Mapa
              </Button>
              <Button
                onClick={() => setMapStyle('satelite')}
                variant={
                  mapStyle === 'satelite'
                    ? 'contained'
                    : 'outlined'
                }
              >
                Satélite
              </Button>
            </ButtonGroup>

            <Tooltip title="Atualizar agora">
              <span>
                <IconButton
                  onClick={handleManualRefresh}
                  disabled={loading}
                >
                  {loading ? (
                    <CircularProgress
                      size={20}
                    />
                  ) : (
                    <RefreshIcon />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Info de última atualização */}
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
          >
            Veículos exibidos:{' '}
            <strong>
              {filteredVeiculos.length}/
              {veiculos.length}
            </strong>
            {lastUpdated && (
              <>
                {' '}
                — Última atualização:{' '}
                {formatDateTimeBR(
                  lastUpdated.getTime()
                )}
              </>
            )}
          </Typography>
        </Box>

        {error && (
          <Alert severity="error">{error}</Alert>
        )}

        <Popover
        open={openGaragem}
        anchorEl={anchorGaragemEl}
        onClose={handleCloseGaragem}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        >
        <Box sx={{ p: 2, maxHeight: 400, maxWidth: 360 }}>
            <Typography variant="subtitle1" gutterBottom>
            Veículos na garagem ({veiculosGaragem.length})
            </Typography>

            {veiculosGaragem.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
                Nenhum veículo identificado na garagem.
            </Typography>
            ) : (
            <Table size="small">
                <TableHead>
                <TableRow>
                    <TableCell align="center">Veículo</TableCell>
                    <TableCell align="center">Linha</TableCell>
                </TableRow>
                </TableHead>

                <TableBody>
                {veiculosGaragem.map((v) => (
                    <TableRow key={v.codigo || v.placa}>
                    <TableCell align="center">
                        {v.codigo || v.placa || '—'}
                    </TableCell>
                    <TableCell align="center">
                        {v.linha || '—'}
                    </TableCell>
                    </TableRow>
                ))}
                </TableBody>
            </Table>
            )}
        </Box>
        </Popover>

        {/* Container do mapa */}
        <Box
          sx={{

            mt: 1,
            height: { xs: 500, md: 650 },
            borderRadius: 3,
            overflow: 'hidden',
            border:
              '1px solid ' +
              theme.palette.divider,
          }}
        >

          <MapContainer
            center={center}
            zoom={zoom}
            style={{
              width: '100%',
              height: '100%',
            }}
            zoomControl={true}
          >
            <RecenterMap center={center} zoom={zoom} />

            <TileLayer
              attribution={
                mapStyle === 'rua'
                  ? '&copy; OpenStreetMap'
                  : 'Tiles &copy; Esri World Imagery'
              }
              url={
                mapStyle === 'rua'
                  ? 'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                  : 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
              }
            />

            {/* Pontos fixos (garagens, terminais, bairros etc.) */}
            {LOCALIZACOES_FIXAS.map((ponto) => (
              <Marker
                key={ponto.id || ponto.nome}
                position={[ponto.latitude, ponto.longitude]}
                icon={createFixedPointIcon(ponto)}
              >
                <LeafletTooltip
                  direction="top"
                  offset={[0, -30]}
                  opacity={0.9}
                >
                  <span>
                    {ponto.nome}
                    {ponto.tipo && (
                      <>
                        <br />
                        <strong>{ponto.tipo}</strong>
                      </>
                    )}
                  </span>
                </LeafletTooltip>

                <Popup>
                  <Box sx={{ minWidth: 200 }}>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                    >
                      <RoomIcon
                        fontSize="small"
                        sx={{ mr: 0.5 }}
                      />
                      <strong>{ponto.nome}</strong>
                    </Typography>

                    {ponto.tipo && (
                      <Typography variant="body2">
                        Tipo:{' '}
                        <strong>
                          {ponto.tipo}
                        </strong>
                      </Typography>
                    )}

                    <Typography variant="body2">
                      Latitude:{' '}
                      <strong>
                        {ponto.latitude}
                      </strong>
                    </Typography>

                    <Typography variant="body2">
                      Longitude:{' '}
                      <strong>
                        {ponto.longitude}
                      </strong>
                    </Typography>
                  </Box>
                </Popup>
              </Marker>
            ))}

            {filteredVeiculos.map((v) => {
              const codigoKey = normCode(v.codigo || v.placa);

              const infoQuebra =
                codigoKey && quebrasPorVeiculo
                  ? quebrasPorVeiculo[codigoKey]
                  : null;
              const isQuebrado =
                !!(infoQuebra && infoQuebra.totalQuebras > 0);

              const infoOcioso =
                codigoKey && ociososPorVeiculo
                  ? ociososPorVeiculo[codigoKey]
                  : null;
              const isOciosoFechado = !!infoOcioso;

              return (
                <Marker
                  key={
                    v.codigo ||
                    v.placa ||
                    `${v.latitude},${v.longitude}`
                  }
                  position={[v.latitude, v.longitude]}
                  icon={createBusIcon(
                    v.velocidade > 0,
                    isQuebrado,
                    isOciosoFechado
                  )}
                >
                  <LeafletTooltip
                    direction="top"
                    offset={[0, -30]}
                    opacity={0.9}
                  >
                    <span>
                      Veículo:{' '}
                      {v.codigo || v.placa || '—'}
                      <br />
                      Linha:{' '}
                      {v.linha || '—'}
                      {isOciosoFechado && (
                        <>
                          <br />
                          <strong>⏸️ Ocioso fechado</strong>
                        </>
                      )}
                      {isQuebrado && (
                        <>
                          <br />
                          <strong>⚠️ Quebra detectada</strong>
                        </>
                      )}
                    </span>
                  </LeafletTooltip>

                  <Popup>
                    <Box
                      sx={{
                        minWidth: 200,
                      }}
                    >
                      <Typography
                        variant="subtitle2"
                        gutterBottom
                      >
                        <RoomIcon
                          fontSize="small"
                          sx={{ mr: 0.5 }}
                        />
                        Veículo{' '}
                        <strong>
                          {v.codigo || v.placa || '—'}
                        </strong>
                      </Typography>

                      {v.placa && (
                        <Typography variant="body2">
                          Placa:{' '}
                          <strong>
                            {v.placa}
                          </strong>
                        </Typography>
                      )}

                      {v.linha && (
                        <Typography variant="body2">
                          Linha:{' '}
                          <strong>
                            {v.linha}
                          </strong>
                        </Typography>
                      )}

                      {v.trajeto && (
                        <Typography variant="body2">
                          Trajeto:{' '}
                          <strong>
                            {v.trajeto}
                          </strong>
                        </Typography>
                      )}

                      <Typography variant="body2">
                        Sentido:{' '}
                        <strong>
                          {v.sentido || '—'}
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Velocidade:{' '}
                        <strong>
                          {v.velocidade.toFixed(0)} km/h
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Ignição:{' '}
                        <strong>
                          {v.ignicao === 1
                            ? 'Ligada'
                            : 'Desligada'}
                        </strong>
                      </Typography>

                      <Typography variant="body2">
                        Última posição:{' '}
                        <strong>
                          {formatDateTimeBR(
                            v.dataHora
                          ) || '—'}
                        </strong>
                      </Typography>

                      {isQuebrado &&
                        infoQuebra &&
                        Array.isArray(infoQuebra.quebras) &&
                        infoQuebra.quebras.length > 0 && (
                          <Box
                            sx={{
                              mt: 1,
                              pt: 1,
                              borderTop:
                                '1px solid rgba(0,0,0,0.12)',
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 600,
                                color: 'error.main',
                              }}
                            >
                              🚨 Quebra detectada
                            </Typography>

                            {infoQuebra.quebras
                              .slice(0, 3)
                              .map((q, idx) => (
                                <Typography
                                  key={idx}
                                  variant="body2"
                                >
                                  •{' '}
                                  {q.duracao ||
                                    'Duração não informada'}{' '}
                                  ({q.horarioInicio || '?'} -{' '}
                                  {q.horarioFim || '?'})
                                </Typography>
                              ))}

                            {infoQuebra.quebras.length >
                              3 && (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                +{' '}
                                {infoQuebra.quebras.length - 3}{' '}
                                ocorrência(s) de quebra
                              </Typography>
                            )}
                          </Box>
                        )}
                    </Box>
                  </Popup>
                </Marker>
              );
            })}
          </MapContainer>
        </Box>
      </Paper>
    </Box>
  );
}
